import { api } from '../../../lib/api';
import type { PortalMe } from '../../../types/portalUser';
import type { PortalInspirationCard } from '../../../types/portalInspiration';
import type { PortalClubSummary } from '../../../types/portalLeadBot';
import type { PortalHomeTeam } from '../../../lib/portalTeam';
import type {
  PortalLibraryOpenResult,
  PortalLibrarySection,
  PortalLibrarySectionResult,
} from '../../../types/portalLibrary';

// CLIENT PORTAL — the portal's OWN API calls.
//
// ⚠️ Everything here hits /api/portal, behind requirePortalAuth. Nothing in the
// portal UI may call a staff endpoint; the split into two files is what makes
// reaching for the wrong one look wrong at the import.
//
// The shared `api()` helper is reused deliberately: it attaches the current
// Firebase ID token and nothing else, which is exactly right for both identity
// pools — the SERVER decides which pool a token belongs to, not the client.

export async function fetchPortalMe(): Promise<PortalMe> {
  return api<PortalMe>('/api/portal/me');
}

/**
 * The Kyck people this client can contact (§4/§5).
 *
 * ⚠️ A SEPARATE CALL FROM `/me`, on its own query key. `/me` is read by the nav,
 * the shell's brand theme and this page off ONE shared key, so folding the team
 * into it would make every portal page pay for a roster scan and a users read.
 */
export async function fetchPortalTeam(): Promise<PortalHomeTeam> {
  return api<PortalHomeTeam>('/api/portal/team');
}

/**
 * One library section's tiles for THIS client (2026-09-23). The server resolves
 * the scope from the session and applies the audience rules; nothing here says
 * who the client is or what they may see.
 */
export async function fetchPortalLibrary(
  section: PortalLibrarySection,
): Promise<PortalLibrarySectionResult> {
  return api<PortalLibrarySectionResult>(`/api/portal/library/${encodeURIComponent(section)}`);
}

/**
 * The Inspiration cards THIS viewer may see (Run I). View only. The server resolves the scope from the
 * session, applies each entry's audience and signs the media; nothing here says who the client is.
 */
export async function fetchPortalInspiration(): Promise<{ cards: PortalInspirationCard[] }> {
  return api<{ cards: PortalInspirationCard[] }>('/api/portal/inspiration');
}

/**
 * The clubs in THIS viewer's scope that have a lead-conversations bot (2026-10-17). Drives the
 * "Lead conversations" nav item: it appears only when there is at least one. The server resolves the
 * scope from the session; a failed or pending fetch counts as "none".
 */
export async function fetchPortalLeadBotClubs(): Promise<{ clubs: PortalClubSummary[] }> {
  return api<{ clubs: PortalClubSummary[] }>('/api/portal/lead-bot/clubs');
}

/**
 * Open ONE library document (Round 3). The server re-checks this viewer against
 * the tile's audience and, on a pass, sets the short-lived grant the document's
 * content route requires. ⚠️ Nothing here names the viewer or their accounts —
 * only which tile — and a 404 (every refusal alike) throws an ApiError the page
 * reads by status.
 */
export async function openPortalLibraryEntry(entryId: string): Promise<PortalLibraryOpenResult> {
  return api<PortalLibraryOpenResult>(
    `/api/portal/library/entry/${encodeURIComponent(entryId)}`,
  );
}

/**
 * The unauthed request-link call. ⚠️ It deliberately does NOT go through `api()`:
 * that helper requires a signed-in Firebase user, and the whole point of this
 * endpoint is that the caller is not signed in yet.
 *
 * The server answers identically whether or not the address has access, so
 * there is nothing here to branch on — and nothing this function could report
 * that would tell the caller more than the server intends to.
 */
export async function requestPortalSignInLink(email: string): Promise<void> {
  const res = await fetch('/api/public/portal/request-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? 'Something went wrong. Try again in a moment.');
  }
}
